// client/src/components/ticketing/OrdersPanel.jsx
//
// Admin order management for one event — the first UI over t_ticket_order.
// List + filters + CSV export; click a row to open OrderDetailDrawer where
// staff can issue a full or partial refund.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import OrderDetailDrawer from './OrderDetailDrawer';

const STATUS_OPTIONS = ['', 'completed', 'refunded', 'pending', 'failed', 'cancelled'];

export default function OrdersPanel({ tenantId, eventId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}`;

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${base}/orders`, { params: { page, limit, status: status || undefined, q: q || undefined } });
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [base, page, status, q]);

  useEffect(() => { load(); }, [load]);

  const money = (n, cur = 'usd') => `${(cur || 'usd').toUpperCase()} ${Number(n || 0).toFixed(2)}`;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Orders</h2>
        <a
          href={`${base}/checkin/roster?format=csv`}
          className="text-sm px-3 py-1.5 rounded border border-black/15 dark:border-white/15"
        >
          Download attendee CSV
        </a>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          placeholder="Search buyer name or email"
          className="flex-1 min-w-[180px] px-3 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
        />
        <select
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || 'All statuses'}</option>)}
        </select>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading ? (
        <div className="text-sm opacity-70">Loading orders…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-black/10 dark:border-white/10 opacity-70">
                <th className="p-2">Buyer</th>
                <th className="p-2">Tier</th>
                <th className="p-2 text-right">Tickets</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-right">Refunded</th>
                <th className="p-2">Status</th>
                <th className="p-2">Placed</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => setOpenId(o.id)}
                  className="border-b border-black/5 dark:border-white/5 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <td className="p-2">
                    <div className="font-medium">{o.buyer_name}</div>
                    <div className="text-xs opacity-60">{o.buyer_email}</div>
                  </td>
                  <td className="p-2">{o.tier_name}</td>
                  <td className="p-2 text-right">{o.num_tickets}</td>
                  <td className="p-2 text-right">{money(o.total_amount, o.currency)}</td>
                  <td className="p-2 text-right">{o.refunded_amount > 0 ? money(o.refunded_amount, o.currency) : '—'}</td>
                  <td className="p-2"><StatusPill status={o.status} /></td>
                  <td className="p-2 text-xs opacity-70">{new Date(o.created_on).toLocaleDateString()}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center opacity-60">No orders match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 rounded border border-black/15 dark:border-white/15 disabled:opacity-40">Prev</button>
          <span className="opacity-70">Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 rounded border border-black/15 dark:border-white/15 disabled:opacity-40">Next</button>
        </div>
      )}

      {openId != null && (
        <OrderDetailDrawer
          tenantId={tenantId}
          eventId={eventId}
          orderId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => { setOpenId(null); load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    completed: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    refunded: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    pending: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
    failed: 'bg-red-500/15 text-red-600 dark:text-red-400',
    cancelled: 'bg-gray-500/15 text-gray-600 dark:text-gray-400',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || map.cancelled}`}>{status}</span>;
}
